import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import { isEmpty, truncate } from 'lodash';
import Popover from 'antd/es/popover';
import cx from 'clsx';

import { ProjectAvatar, ProposalFlag } from '@/components';
import { useDeviceTypeIsMobile } from '@/hooks';
import { shortenAddress, openNewWindow } from '@/utils';

import { TokenHistoryItem, CexDetail, TransactionProject } from '../../types';
import style from './index.module.scss';

interface HistoryRowProps {
  info: TokenHistoryItem;
  projectsDict: Record<string, TransactionProject>;
  cexs: Record<string, CexDetail>;
  memos: Record<string, string>;
  tokensDict: Record<string, Tokens>;
  cate: {
    [index: string]: {
      name: string;
      id: string;
    };
  };
  projectAvatarSize?: number;
  showChain?: boolean;
  showChainTip?: boolean;
  className?: string;
  actionClassName?: string;
  projectNameClassName?: string;
}

export const TransactionAction = (props: HistoryRowProps) => {
  const { t } = useTranslation();

  const {
    info,
    projectsDict,
    cexs,
    cate,
    tokensDict,
    memos,
    projectAvatarSize = 28,
    showChain,
    className,
    actionClassName,
    projectNameClassName,
    showChainTip,
  } = props;

  const isMobile = useDeviceTypeIsMobile();

  const isCancel = useMemo(() => info.cate_id === 'cancel', [info.cate_id]);
  const isApprove = useMemo(() => info.cate_id === 'approve', [info.cate_id]);

  const isDisplayCex = useMemo(() => {
    if (!cexs[info.other_addr]) return false;

    const isSend = !isEmpty(info.sends);
    const isReceive = !isEmpty(info.receives);
    const isDeposit = cexs[info.other_addr]?.is_deposit;

    return (isDeposit && isSend) || (!isDeposit && isReceive);
  }, [cexs, info.other_addr, info.receives, info.sends]);

  const isDisplayMemo = useMemo(() => {
    return (info.cate_id === 'send' || info.cate_id === 'receive') && memos[info.other_addr];
  }, [info.cate_id, info.other_addr, memos]);

  const project = useMemo(() => {
    return isDisplayCex ? cexs[info.other_addr] : projectsDict[info.project_id || info.other_addr];
  }, [projectsDict, cexs, info.project_id, info.other_addr, isDisplayCex]);

  const openOtherAddr = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      openNewWindow(`/profile/${info.other_addr}`, '_blank', true);
    },
    [info.other_addr],
  );

  const projectName = useMemo(() => {
    const name = project?.name ?? shortenAddress(info.other_addr || '');

    return (
      <span className={cx(style.projectName, projectNameClassName)} onClick={openOtherAddr}>
        {name}
        {isDisplayMemo ? ` (${truncate(memos[info.other_addr], { length: 20 })})` : null}
      </span>
    );
  }, [info.other_addr, project?.name, isDisplayMemo, memos, projectNameClassName, openOtherAddr]);

  const interAddressExplain = useMemo(() => {
    if (isDisplayCex) {
      const isDeposit = project?.is_deposit;
      const isSend = !isEmpty(info.sends);
      const isReceive = !isEmpty(info.receives);
      let text;
      if (isDeposit && isSend) {
        text = t('TransactionAction.deposit', { name: project?.name });
      }
      if (!isDeposit && isReceive) {
        text = t('TransactionAction.withdraw', { name: project?.name });
      }

      return (
        <div>
          <div className={cx(style.action, actionClassName)}>{text}</div>
          <div className={cx(style.textHighlight, projectNameClassName)} onClick={openOtherAddr}>
            {shortenAddress(info.other_addr || '')}
          </div>
        </div>
      );
    }

    if (isCancel) {
      return <span className={actionClassName}>{t('TransactionAction.139x1m7')}</span>;
    }

    if (isApprove) {
      const amount = info.token_approve?.value || 0;
      const approveToken =
        tokensDict[info.token_approve?.token_id || ''] ||
        tokensDict[`${info.chain}_token:${info.token_approve?.token_id}`];

      return (
        <div className={actionClassName}>
          <Trans
            i18nKey="TransactionAction.approveDesc"
            values={{
              amount: amount < 1e9 ? amount.toFixed(4) : t('TransactionAction.infinite'),
              token: approveToken?.display_symbol || approveToken?.symbol,
            }}
            components={{ projectName }}
          />
        </div>
      );
    }

    return (
      <div>
        <div className={cx(style.action, actionClassName)}>
          {cate[info.cate_id || '']?.name ?? (info.tx?.name || t('TransactionAction.contract'))}
        </div>
        <div className={cx(style.textHighlight, projectNameClassName)}>{projectName}</div>
      </div>
    );
  }, [
    cate,
    info,
    isApprove,
    isCancel,
    isDisplayCex,
    project,
    projectName,
    tokensDict,
    actionClassName,
    projectNameClassName,
    openOtherAddr,
    t,
  ]);

  const customProposalFlag = useMemo(() => {
    if (project?.id.endsWith('unknown_protocol')) {
      return {
        initType: 'DeFi Assets',
        initSubType: 'Update protocol info',
        initExtra: project?.name,
        initAddrs: info.other_addr,
        initRule: {
          required: ['urls', 'addrs'],
        },
      };
    }

    if (
      isDisplayCex ||
      (info.cate_id && ['cancel', 'receive', 'send', 'approve'].indexOf(info.cate_id) > -1)
    ) {
      return;
    }

    return {
      initType: 'DeFi Assets',
      initSubType: 'Support new pool in protocol',
      initExtra: project?.name,
      initAddrs: info.other_addr,
      initUrls: (project as TransactionProject)?.site_url,
    };
  }, [project, info, isDisplayCex]);

  return (
    <ProposalFlag {...customProposalFlag} disabled={!customProposalFlag} offsetX={12}>
      <div className={cx(style.transactionAction, isMobile && style.isMobile, className)}>
        <ProjectAvatar
          url={project?.logo_url}
          type={info.cate_id}
          chain={showChain ? info.chain : undefined}
          size={projectAvatarSize}
          className={style.projectAvatar}
          showChainTip={showChainTip}
        />
        <div className={style.projectInfo}>
          {project?.name || info.other_addr ? (
            <Popover
              content={
                <div className={style.pop}>
                  {project && <div>{project.name}</div>}
                  <div className={style.addr}>{info.other_addr} </div>
                </div>
              }
              overlayClassName={style.popSD}>
              {interAddressExplain}
            </Popover>
          ) : (
            interAddressExplain
          )}
        </div>
      </div>
    </ProposalFlag>
  );
};
